import { Fragment, ReactNode, useMemo } from "react";
import { AlertCircle, Check } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";

import { cn } from "@/lib/utils";

export interface WizardStepperItem {
  id: string;
  title: string;
  shortTitle?: string;
  icon?: ReactNode;
  /** Fase à qual o passo pertence (ativa o modo agrupado quando todos a definem). */
  group?: string;
}

interface WizardStepperProps {
  steps: WizardStepperItem[];
  currentStep: number;
  /** Passo mais avançado já alcançado — passos até ele ficam clicáveis. */
  furthestStep: number;
  onStepClick: (index: number) => void;
  /** Por passo: há campo pendente/inválido? Marca o passo no stepper. */
  errorSteps?: boolean[];
}

interface Phase {
  group: string;
  /** Índices (na lista global de passos) que compõem a fase, em ordem. */
  indices: number[];
}

/** Agrupa passos consecutivos que compartilham o mesmo `group`, preservando a ordem. */
function buildPhases(steps: WizardStepperItem[]): Phase[] {
  const phases: Phase[] = [];
  steps.forEach((step, index) => {
    const group = step.group ?? "";
    const last = phases[phases.length - 1];
    if (last && last.group === group) last.indices.push(index);
    else phases.push({ group, indices: [index] });
  });
  return phases;
}

/**
 * Indicador de progresso do wizard.
 *
 * Desktop:
 * - **Plano** (padrão): trilha horizontal com uma bolinha por passo.
 * - **Agrupado** (quando todo passo tem `group`): trilha com um marcador por
 *   fase (ex. Dados do Job · Planilha · Revisão) e, abaixo, os sub-passos apenas
 *   da fase atual — cortando o excesso de bolinhas em fluxos longos.
 * Mobile: título do passo + barra segmentada (um segmento por passo).
 */
export function WizardStepper({
  steps,
  currentStep,
  furthestStep,
  onStepClick,
  errorSteps,
}: WizardStepperProps) {
  const prefersReducedMotion = useReducedMotion();
  const current = steps[currentStep];
  // Passos com pendência, exceto o atual (que o usuário está editando agora).
  const pendingTitles = steps
    .filter((_, i) => i !== currentStep && errorSteps?.[i])
    .map((s) => s.shortTitle ?? s.title);

  // Modo agrupado só quando TODOS os passos declaram uma fase — senão a trilha
  // plana continua valendo (consumidores que não optaram pelo agrupamento).
  const grouped = steps.length > 0 && steps.every((s) => Boolean(s.group));
  const phases = useMemo(() => (grouped ? buildPhases(steps) : []), [grouped, steps]);
  const currentPhaseIdx = grouped ? phases.findIndex((p) => p.indices.includes(currentStep)) : -1;
  const currentPhase = phases[currentPhaseIdx];

  return (
    <div className="w-full">
      {/* Mobile/tablet: título (com a fase, se houver) + barra segmentada. */}
      <div className="space-y-2 lg:hidden">
        <div className="flex items-baseline justify-between gap-3">
          <span className="flex items-baseline gap-1.5 text-sm font-semibold text-foreground">
            {grouped && current?.group && (
              <span className="text-xs font-medium text-muted-foreground">{current.group} ·</span>
            )}
            {current?.shortTitle ?? current?.title}
          </span>
          <span className="shrink-0 text-xs font-medium text-muted-foreground">
            Passo {currentStep + 1} de {steps.length}
          </span>
        </div>
        <div className="flex gap-1.5" role="list" aria-label="Progresso do formulário">
          {steps.map((step, index) => {
            const isDone = index <= currentStep;
            const isReachable = index <= furthestStep;
            const hasError = index !== currentStep && Boolean(errorSteps?.[index]);

            return (
              <button
                key={step.id}
                type="button"
                role="listitem"
                disabled={!isReachable}
                onClick={() => isReachable && onStepClick(index)}
                aria-label={`Passo ${index + 1}: ${step.title}`}
                aria-current={index === currentStep ? "step" : undefined}
                className={cn(
                  "h-1.5 flex-1 rounded-full transition-colors duration-500",
                  isReachable ? "cursor-pointer" : "cursor-default",
                  hasError ? "bg-destructive" : isDone ? "bg-primary" : "bg-muted",
                )}
              />
            );
          })}
        </div>
        <AnimatePresence initial={false}>
          {pendingTitles.length > 0 && (
            <motion.p
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="flex items-center gap-1.5 overflow-hidden text-xs font-medium text-destructive"
            >
              <AlertCircle className="size-3.5 shrink-0" />
              Revise: {pendingTitles.join(", ")}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Desktop (lg+) agrupado: trilha de fases + sub-passos da fase atual */}
      {grouped ? (
        <div className="hidden space-y-4 lg:block">
          <ol className="flex items-center">
            {phases.map((phase, pIdx) => {
              const start = phase.indices[0];
              const end = phase.indices[phase.indices.length - 1];
              const isCurrent = pIdx === currentPhaseIdx;
              const isDone = currentStep > end;
              const isReachable = start <= furthestStep;
              const isLast = pIdx === phases.length - 1;
              const hasError = !isCurrent && phase.indices.some((i) => errorSteps?.[i]);

              return (
                <Fragment key={phase.group || pIdx}>
                  <li className="flex items-center">
                    <button
                      type="button"
                      disabled={!isReachable}
                      onClick={() => isReachable && onStepClick(start)}
                      className={cn(
                        "flex items-center gap-3 rounded-lg px-1 py-1 text-left transition-colors",
                        isReachable ? "cursor-pointer" : "cursor-default",
                      )}
                      aria-current={isCurrent ? "step" : undefined}
                    >
                      <motion.span
                        animate={{ scale: !prefersReducedMotion && isCurrent ? 1.08 : 1 }}
                        transition={{ type: "spring", stiffness: 400, damping: 22 }}
                        className={cn(
                          "flex size-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors",
                          hasError && "border-destructive bg-destructive/10 text-destructive",
                          !hasError &&
                            isCurrent &&
                            "border-primary bg-card text-primary ring-4 ring-primary/15",
                          !hasError &&
                            isDone &&
                            "border-primary bg-primary text-primary-foreground",
                          !hasError &&
                            !isCurrent &&
                            !isDone &&
                            "border-border bg-card text-muted-foreground",
                        )}
                      >
                        {hasError ? (
                          <AlertCircle className="size-4" />
                        ) : isDone ? (
                          <Check className="size-4" />
                        ) : (
                          pIdx + 1
                        )}
                      </motion.span>
                      <span className="flex flex-col">
                        <span
                          className={cn(
                            "text-sm font-semibold transition-colors",
                            hasError
                              ? "text-destructive"
                              : isCurrent
                                ? "text-foreground"
                                : "text-muted-foreground",
                          )}
                        >
                          {phase.group}
                        </span>
                        {isCurrent && phase.indices.length > 1 && (
                          <span className="text-xs font-medium text-muted-foreground">
                            Passo {phase.indices.indexOf(currentStep) + 1} de {phase.indices.length}
                          </span>
                        )}
                      </span>
                    </button>
                  </li>

                  {!isLast && (
                    <li
                      className="mx-3 h-0.5 flex-1 overflow-hidden rounded-full bg-border"
                      aria-hidden
                    >
                      <span
                        className={cn(
                          "block h-full rounded-full bg-primary transition-all duration-500 ease-out",
                          isDone ? "w-full" : "w-0",
                        )}
                      />
                    </li>
                  )}
                </Fragment>
              );
            })}
          </ol>

          {/* Sub-passos: só da fase atual e só quando ela tem mais de um. */}
          {currentPhase && currentPhase.indices.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 pl-1">
              {currentPhase.indices.map((index) => {
                const step = steps[index];
                const isCur = index === currentStep;
                const isDone = index < currentStep;
                const isReachable = index <= furthestStep;
                const hasError = !isCur && Boolean(errorSteps?.[index]);

                return (
                  <button
                    key={step.id}
                    type="button"
                    disabled={!isReachable}
                    onClick={() => isReachable && onStepClick(index)}
                    aria-current={isCur ? "step" : undefined}
                    className={cn(
                      "flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                      isReachable ? "cursor-pointer" : "cursor-default",
                      hasError && "border-destructive/40 bg-destructive/10 text-destructive",
                      !hasError && isCur && "border-primary bg-primary/10 text-primary",
                      !hasError && isDone && "border-primary/30 bg-card text-foreground",
                      !hasError &&
                        !isCur &&
                        !isDone &&
                        "border-border bg-card text-muted-foreground",
                    )}
                  >
                    <span
                      className={cn(
                        "flex size-4 shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold",
                        hasError && "border-destructive text-destructive",
                        !hasError && isCur && "border-primary text-primary",
                        !hasError && isDone && "border-primary bg-primary text-primary-foreground",
                        !hasError && !isCur && !isDone && "border-muted-foreground/40",
                      )}
                    >
                      {hasError ? "!" : isDone ? <Check className="size-2.5" /> : null}
                    </span>
                    {step.shortTitle ?? step.title}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Desktop (lg+) plano: trilha horizontal clicável (um marcador por passo) */
        <ol className="hidden items-center lg:flex">
          {steps.map((step, index) => {
            const isCurrent = index === currentStep;
            const isDone = index < currentStep;
            const isReachable = index <= furthestStep;
            const isLast = index === steps.length - 1;
            const hasError = !isCurrent && Boolean(errorSteps?.[index]);

            return (
              <Fragment key={step.id}>
                <li className="flex items-center gap-3">
                  <button
                    type="button"
                    disabled={!isReachable}
                    onClick={() => isReachable && onStepClick(index)}
                    className={cn(
                      "group flex items-center gap-3 rounded-lg px-1 py-1 text-left transition-colors",
                      isReachable ? "cursor-pointer" : "cursor-default",
                    )}
                    aria-current={isCurrent ? "step" : undefined}
                  >
                    <motion.span
                      animate={{ scale: !prefersReducedMotion && isCurrent ? 1.08 : 1 }}
                      transition={{ type: "spring", stiffness: 400, damping: 22 }}
                      className={cn(
                        "flex size-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors",
                        hasError && "border-destructive bg-destructive/10 text-destructive",
                        !hasError &&
                          isCurrent &&
                          "border-primary bg-card text-primary ring-4 ring-primary/15",
                        !hasError && isDone && "border-primary bg-primary text-primary-foreground",
                        !hasError &&
                          !isCurrent &&
                          !isDone &&
                          "border-border bg-card text-muted-foreground",
                      )}
                    >
                      {hasError ? (
                        <AlertCircle className="size-4" />
                      ) : isDone ? (
                        <Check className="size-4" />
                      ) : (
                        (step.icon ?? index + 1)
                      )}
                    </motion.span>
                    <span
                      className={cn(
                        "text-sm font-medium transition-colors",
                        hasError
                          ? "text-destructive"
                          : isCurrent
                            ? "text-foreground"
                            : "text-muted-foreground",
                      )}
                    >
                      {step.title}
                    </span>
                  </button>
                </li>

                {!isLast && (
                  <li
                    className="mx-3 h-0.5 flex-1 overflow-hidden rounded-full bg-border"
                    aria-hidden
                  >
                    <span
                      className={cn(
                        "block h-full rounded-full bg-primary transition-all duration-500 ease-out",
                        index < currentStep ? "w-full" : "w-0",
                      )}
                    />
                  </li>
                )}
              </Fragment>
            );
          })}
        </ol>
      )}
    </div>
  );
}
